<template>
  <Labelled
    :label="label"
    :error="errorMessage"
    :help-text="helpText"
    :help-text-html="helpTextHtml"
  >
    <Popper
      placement="bottom-start"
      ref="popper"
      auto-hide
      z-index="2005"
    >
      <template #trigger="{show, hide}">
        <div :class="$style.DatePicker_inputWrapper">
          <div
            v-if="clearable"
            :class="$style.DatePicker_clearAction"
            @click="clearValue"
          >
            <Icon icon="fa fa-times-circle" />
          </div>
          <input
            :class="[
              $style.DatePicker_input,
              errorMessage && $style.DatePicker_input__hasError
            ]"
            :value="inputValue"
            :placeholder="placeholder"
            :disabled="disabled"
            :readonly="readonly"
            @input="inputValue = $event.target.value"
            @change="setFromInput($event.target.value)"
            @click="attemptShow(show)"
            @focus="attemptShow(show)"
            @keydown.enter="setFromInput($event.target.value)"
            @keydown.tab="hide"
            type="text"
          />
        </div>
      </template>
      <div :class="$style.DatePicker_popper">
        <Calendar
          :model-format="modelFormat"
          :value="value"
          :with-time="withTime"
          @change="val => setFromCalendar(val)"
        />
      </div>
    </Popper>
  </Labelled>
</template>

<script>
import moment from 'moment'
import {Calendar} from '../Calendar'
import {Icon} from '../Icon'
import {Labelled} from '../Labelled'
import {Popper} from '../Popper'
import {getContextInjections} from '../../utils/vm'

export default {
  name: 'DatePicker',
  components: {
    Calendar,
    Icon,
    Labelled,
    Popper
  },
  inject: {
    ...getContextInjections()
  },
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    label: {
      type: String
    },
    error: {
      type: String
    },
    helpText: {
      type: String
    },
    helpTextHtml: {
      type: String
    },
    /**
     * The underlying model value represented as `YYYY-MM-DD`.
     */
    value: {
      type: String
    },
    placeholder: {
      type: String
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    /**
     * Display format of the picker.
     *
     * @see https://momentjs.com/docs/#/displaying/format/
     */
    format: {
      type: String,
      default: null
    },
    /**
     * Format of the underling value.
     *
     * @see https://momentjs.com/docs/#/displaying/format/
     */
    modelFormat: {
      type: String,
      default: 'YYYY-MM-DD HH:mm:ss'
    },
    withTime: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      inputValue: null
    }
  },
  created() {
    this.$watch('value', function(value) {
      if (value) {
        const formatted = moment(value, this.modelFormat, true)
          .format(this.displayFormat)
        this.inputValue = formatted
      } else {
        this.inputValue = value
      }
    }, {immediate: true})
  },
  computed: {
    displayFormat() {
      return this.format || (this.withTime ? 'L LT' : 'L')
    },
    usageFormat() {
      return this.displayFormat
        .split(' ')
        .map(token => {
          return moment
            .localeData()
            .longDateFormat(token)
        })
        .join(' ')
    },
    errorMessage() {
      const {inputValue, displayFormat} = this
      if (inputValue) {
        const mDate = moment(inputValue, displayFormat, true)
        if (!mDate.isValid()) {
          return `Invalid date format, please use ${this.usageFormat}`
        }
      }
      return this.error
    }
  },
  methods: {
    setFromInput(text) {
      const mDate = moment(text, this.displayFormat, true)
      if (mDate.isValid()) {
        const value = mDate.format(this.modelFormat)
        this.$emit('change', value)
      } else {
        this.$emit('change', null)
      }
      this.$refs.popper.hide()
    },
    setFromCalendar(date) {
      this.$emit('change', date)
      if (!this.withTime) {
        this.$refs.popper.hide()
      }
    },
    clearValue() {
      this.$emit('change', null)
    },
    attemptShow(showFn) {
      if (!this.disabled && !this.readonly) {
        showFn()
      }
    }
  }
}
</script>

<style lang="scss" module>
@import "../../styles/variables";
@import "../../styles/mixins";

.DatePicker_inputWrapper {
  position: relative;
}

.DatePicker_clearAction {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: $inputMinHeight;
  height: $inputMinHeight;
  color: #666;
  // outline: 1px solid red;

  &:hover {
    cursor: pointer;
    color: #000;
  }
}

.DatePicker_input {
  &:read-only {
    @include inputBaseReadonly;
  }
  &:disabled {
    @include inputBaseDisabled;
  }
  @include input;
}

.DatePicker_input__hasError {
  @include inputBaseError;

  &:focus {
    @include inputBaseErrorFocus;
  }
}

.DatePicker_popper {
  margin: 8px 0;
  padding: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 3px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}
</style>
